import {
  faCheck,
  faInfoCircle,
  faLockKeyhole,
} from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { Link } from '@sorare/routing';

import { MonetaryAmount } from '__generated__/globalTypes';
import { FontAwesomeIcon } from 'atoms/icons';
import { Drawer } from 'atoms/layout/Drawer';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LabelL, LabelM, LabelS } from 'atoms/typography';
import { Chip } from 'atoms/ui/Chip';
import { Dialog } from 'components/dialog';
import { ConfigQuery } from 'contexts/config/types';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useAmountWithConversion } from 'hooks/useAmountWithConversion';
import { isType } from 'lib/gql';

import { Cta, Label } from '../Cta';
import { Remaining } from '../Remaining';
import cover from './assets/cover.jpg';
import creditCoin from './assets/credit-coin.png';
import boxImg from './assets/reward.png';

const Line = styled.div<{ active: boolean }>`
  position: absolute;
  width: var(--unit);
  top: calc(var(--size) - 1px);
  bottom: -1px;
  left: calc(var(--size) / 2);
  transform: translateX(-50%);
  background-color: var(--c-nl-600-opaque);

  ${({ active }) =>
    active &&
    css`
      background-color: var(--c-white);
    `}
`;

const StepRoot = styled(Horizontal).attrs({ gap: 1.5 })`
  width: 100%;
  align-items: flex-start;
  position: relative;
  min-height: 50px;
  padding-bottom: var(--triple-unit);
  --size: var(--triple-unit);

  &:last-child {
    padding-bottom: 0;
    ${Line} {
      display: none;
    }
  }
`;

const Pin = styled(Horizontal).attrs({ gap: 0 })<{ active: boolean }>`
  justify-content: center;
  background-color: var(--c-nl-600-opaque);
  width: var(--size);
  height: var(--size);
  flex-shrink: 0;
  z-index: 1;
  border-radius: 50%;
  color: var(--c-brand-600);
  ${({ active }) =>
    active &&
    css`
      background-color: var(--c-white);
    `}
`;

const Steps = styled.div`
  padding: var(--double-unit);
  background: linear-gradient(
    84.1deg,
    #3c3639 0%,
    #34323e 28.32%,
    #31353f 54.01%,
    #1e2b3f 100%
  );
`;

const BoxImg = styled.img`
  align-self: flex-start;
`;

const Step = ({
  index,
  state,
  children,
}: {
  index: number;
  state: 'init' | 'inprogress' | 'completed';
  children: ReactNode;
}) => {
  const renderLabel = () => {
    if (state === 'completed') {
      return <FontAwesomeIcon icon={faCheck} size="xs" />;
    }
    if (state === 'inprogress') {
      return <FontAwesomeIcon icon={faLockKeyhole} size="xs" />;
    }
    return (
      <LabelL color="var(--c-white)" bold>
        {index}
      </LabelL>
    );
  };
  return (
    <StepRoot>
      <Pin active={['inprogress', 'completed'].includes(state)}>
        {renderLabel()}
      </Pin>
      <Line active={state === 'completed'} />
      {children}
    </StepRoot>
  );
};

type DetailsContentProps = {
  event: NonNullable<ConfigQuery['currentUser']>['completablePromotionalEvent'];
};

const Header = styled.div`
  width: 100%;
  aspect-ratio: 6 / 2;
  background-image: url(${cover});
  background-position: center top;
  background-size: cover;
`;

export const getCCRewardRange = (
  rewardsProbabilisticBundleConfig: NonNullable<
    NonNullable<ConfigQuery['currentUser']>['completablePromotionalEvent']
  >['rewardsProbabilisticBundleConfig']
) => {
  const allConversionCreditAmounts = rewardsProbabilisticBundleConfig
    .flatMap(bundle => bundle.slots)
    .flatMap(slot => slot.probableConfigs)
    .flatMap(probableConfig => probableConfig.conversionCredit?.maxDiscount)
    .filter(Boolean);

  if (!allConversionCreditAmounts.length) {
    return undefined;
  }
  const refCurrency =
    allConversionCreditAmounts[0].referenceCurrency.toLowerCase() as
      | 'eur'
      | 'usd'
      | 'gbp'
      | 'wei';

  const { max, min } = allConversionCreditAmounts.reduce<{
    max: MonetaryAmount;
    min: MonetaryAmount;
  }>(
    (acc, value) => ({
      max:
        Number(acc.max[refCurrency]) > Number(value[refCurrency])
          ? acc.max
          : value,
      min:
        Number(acc.min[refCurrency]) < Number(value[refCurrency])
          ? acc.min
          : value,
    }),
    { max: allConversionCreditAmounts[0], min: allConversionCreditAmounts[0] }
  );
  return { max, min };
};

const RewardRange = ({
  min,
  max,
}: {
  min: MonetaryAmount;
  max: MonetaryAmount;
}) => {
  const { main } = useAmountWithConversion({ monetaryAmount: min });
  const { main: mainMax } = useAmountWithConversion({ monetaryAmount: max });
  return (
    <FormattedMessage
      id="AddFundsToFiatWallet.Promo.Details.MysteryJersey"
      defaultMessage="{min} to {max} in credits"
      values={{
        min: main,
        max: mainMax,
      }}
    />
  );
};

const DetailsContent = ({ event }: DetailsContentProps) => {
  const { formatNumber } = useIntl();
  const { fiatCurrency } = useCurrentUserContext();

  if (!event || !isType(event, 'DepositPromotionalEvent')) return null;

  const hasDeposit = !!event?.promotionalEventCompletion;

  const minimumDepositAmount =
    (event.eligibilityRules.minimumDepositAmount || 0) / 100;

  const ccRange = getCCRewardRange(event.rewardsProbabilisticBundleConfig);

  return (
    <Vertical gap={0}>
      <Header />
      <Steps>
        <Step index={1} state={hasDeposit ? 'completed' : 'inprogress'}>
          <Vertical grow>
            <LabelL bold>
              <Label minimumAmount={minimumDepositAmount} />
            </LabelL>
            <LabelM color="var(--c-nd-600)">
              <FormattedMessage
                id="AddFundsToFiatWallet.Promo.Details.onlyOnce"
                defaultMessage="Available only on your first deposit of {minimumAmount} or more!"
                values={{
                  minimumAmount: formatNumber(minimumDepositAmount, {
                    style: 'currency',
                    currency: fiatCurrency.code,
                    minimumFractionDigits: 0,
                  }),
                }}
              />
            </LabelM>
            <Cta />
          </Vertical>
        </Step>
        <Step
          index={2}
          state={event.promotionalEventCompletion ? 'inprogress' : 'init'}
        >
          <LabelL bold>
            <FormattedMessage
              id="AddFundsToFiatWallet.Promo.Details.spendHalf"
              defaultMessage="Spend at least {percentage, number, ::percent} of your deposit in Instant Buy or Auctions"
              values={{
                percentage: event.completionRules.completion_percentage / 100,
              }}
            />
          </LabelL>
        </Step>
        <Step index={3} state="init">
          <Vertical>
            <Remaining completedParticipants={event.completedParticipants} />
            <Horizontal>
              <BoxImg alt="" src={boxImg} width="40px" height="40px" />
              <Vertical>
                <LabelL bold>
                  <FormattedMessage
                    id="AddFundsToFiatWallet.Promo.Details.winRewardBox"
                    defaultMessage="Win Reward Box"
                  />
                </LabelL>
                <Chip size="medium">
                  <Vertical>
                    {ccRange && (
                      <Horizontal>
                        <img
                          alt=""
                          src={creditCoin}
                          width="20px"
                          height="20px"
                        />
                        <LabelS bold>
                          <RewardRange {...ccRange} />
                        </LabelS>
                      </Horizontal>
                    )}

                    <Link
                      to={event.blogUrl || ''}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Horizontal>
                        <FontAwesomeIcon icon={faInfoCircle} />
                        <LabelS color="var(--c-nd-600-opaque)" as="span">
                          <FormattedMessage
                            id="AddFundsToFiatWallet.Promo.Details.seeOdds"
                            defaultMessage="See odds"
                          />
                        </LabelS>
                      </Horizontal>
                    </Link>
                  </Vertical>
                </Chip>
              </Vertical>
            </Horizontal>
          </Vertical>
        </Step>
      </Steps>
    </Vertical>
  );
};

export const Details = ({
  open,
  onClose,
  ...props
}: {
  open: boolean;
  onClose: () => void;
} & Pick<DetailsContentProps, 'event'>) => {
  const isTablet = useIsLaptop();
  if (isTablet) {
    return (
      <Dialog hideHeader maxWidth="xs" open={open} onClose={onClose}>
        <DetailsContent {...props} />
      </Dialog>
    );
  }
  return (
    <Drawer open={open} onBackdropClick={onClose} side="bottom">
      <DetailsContent {...props} />
    </Drawer>
  );
};
